export const CROP_RATIO_MARGIN_OF_ERROR = 0.001;

export function normalizeRatioValue(value) {
  if (typeof value === "string" && value.trim() === "") {
    return null;
  }

  const normalizedValue =
    typeof value === "number" ? value : Number(value);

  return Number.isFinite(normalizedValue) ? normalizedValue : null;
}

export function trackEvent(eventName, properties = {}) {
  if (typeof window === "undefined") {
    return;
  }

  if (window.MixpanelTracker?.track) {
    window.MixpanelTracker.track(eventName, properties);
  } else if (typeof window.trackEvent === "function") {
    window.trackEvent(eventName, properties);
  } else if (window.analytics?.track) {
    window.analytics.track(eventName, properties);
  }
}

export function captureSentryMessage(message, properties = {}) {
  if (typeof window === "undefined" || !window.Sentry?.captureMessage) {
    return;
  }

  if (typeof window.Sentry.withScope === "function") {
    window.Sentry.withScope((scope) => {
      if (typeof scope.setLevel === "function") {
        scope.setLevel("error");
      }
      if (typeof scope.setFingerprint === "function") {
        scope.setFingerprint([
          "crop-ratio-error",
          properties.component || "unknown",
          properties.location_pathname || "unknown",
        ]);
      }
      if (typeof scope.setTag === "function") {
        scope.setTag("event_name", message);
        scope.setTag("component", properties.component || "unknown");
        scope.setTag("validation_source", properties.validation_source || "frontend");
        scope.setTag("is_mobile", String(Boolean(properties.is_mobile)));
      }
      if (typeof scope.setContext === "function") {
        scope.setContext("crop_ratio_error", properties);
      }

      window.Sentry.captureMessage(message);
    });
    return;
  }

  window.Sentry.captureMessage(message);
}

export function cropRatioMatches(
  currentCropRatio,
  expectedRatio,
  marginOfError = CROP_RATIO_MARGIN_OF_ERROR,
) {
  const normalizedCurrentCropRatio = normalizeRatioValue(currentCropRatio);
  const normalizedExpectedRatio = normalizeRatioValue(expectedRatio);

  if (
    !Number.isFinite(normalizedCurrentCropRatio) ||
    !Number.isFinite(normalizedExpectedRatio) ||
    normalizedExpectedRatio <= 0
  ) {
    return false;
  }

  return (
    Math.abs(normalizedCurrentCropRatio - normalizedExpectedRatio) <=
    marginOfError
  );
}

const DISPLAY_RATIO_TOLERANCE = 0.15;
const DISPLAY_SQUARE_TOLERANCE = 0.05;

function relativeRatioDifference(left, right) {
  return Math.abs(left - right) / left;
}

export function aspectRatiosAreInverted(
  left,
  right,
  tolerance = DISPLAY_RATIO_TOLERANCE,
) {
  if (
    !Number.isFinite(left) ||
    !Number.isFinite(right) ||
    left <= 0 ||
    right <= 0
  ) {
    return false;
  }

  if (
    Math.abs(left - 1) <= DISPLAY_SQUARE_TOLERANCE ||
    Math.abs(right - 1) <= DISPLAY_SQUARE_TOLERANCE
  ) {
    return false;
  }

  const inverted = relativeRatioDifference(left, 1 / right) <= tolerance;
  const same = relativeRatioDifference(left, right) <= tolerance;
  return inverted && !same;
}

export function displayImageSize({
  imageWidth,
  imageHeight,
  previewWidth,
  previewHeight,
} = {}) {
  const storedWidth = Number(imageWidth);
  const storedHeight = Number(imageHeight);
  const previewW = Number(previewWidth);
  const previewH = Number(previewHeight);

  if (
    Number.isFinite(storedWidth) &&
    Number.isFinite(storedHeight) &&
    Number.isFinite(previewW) &&
    Number.isFinite(previewH) &&
    storedWidth > 0 &&
    storedHeight > 0 &&
    previewW > 0 &&
    previewH > 0 &&
    aspectRatiosAreInverted(storedWidth / storedHeight, previewW / previewH)
  ) {
    return { width: storedHeight, height: storedWidth, swapped: true };
  }

  return {
    width: Number.isFinite(storedWidth) && storedWidth > 0 ? storedWidth : null,
    height:
      Number.isFinite(storedHeight) && storedHeight > 0 ? storedHeight : null,
    swapped: false,
  };
}

export function scaleCropPixelsToDisplaySpace({
  crop,
  imageWidth,
  imageHeight,
  previewWidth,
  previewHeight,
} = {}) {
  const resolvedPreviewWidth = Number(previewWidth);
  const resolvedPreviewHeight = Number(previewHeight);
  const values = [crop?.x, crop?.y, crop?.width, crop?.height].map(Number);

  if (
    !values.every(Number.isFinite) ||
    !Number.isFinite(resolvedPreviewWidth) ||
    !Number.isFinite(resolvedPreviewHeight) ||
    resolvedPreviewWidth <= 0 ||
    resolvedPreviewHeight <= 0
  ) {
    return null;
  }

  const displaySize = displayImageSize({
    imageWidth,
    imageHeight,
    previewWidth: resolvedPreviewWidth,
    previewHeight: resolvedPreviewHeight,
  });
  if (!displaySize.width || !displaySize.height) return null;

  const scale = displaySize.width / resolvedPreviewWidth;

  return {
    cx: Math.round(values[0] * scale),
    cy: Math.round(values[1] * scale),
    cw: Math.round(values[2] * scale),
    ch: Math.round(values[3] * scale),
    cropSpaceWidth: displaySize.width,
    cropSpaceHeight: displaySize.height,
  };
}

export function cropCoordinateSpaceWidth(image = {}) {
  const cropSpaceWidth = Number(image.crop_space_width);
  if (Number.isFinite(cropSpaceWidth) && cropSpaceWidth > 0) {
    return cropSpaceWidth;
  }

  const sourceWidth = Number(image.width);
  return Number.isFinite(sourceWidth) && sourceWidth > 0 ? sourceWidth : null;
}

export function resolvePreviewSize({
  previewWidth,
  previewHeight,
  imageWidth,
  imageHeight,
} = {}) {
  const hasPreviewWidth = Number.isFinite(previewWidth) && previewWidth > 0;
  const hasPreviewHeight = Number.isFinite(previewHeight) && previewHeight > 0;
  const hasImageWidth = Number.isFinite(imageWidth) && imageWidth > 0;
  const hasImageHeight = Number.isFinite(imageHeight) && imageHeight > 0;

  if (hasPreviewWidth && hasPreviewHeight) {
    return { previewWidth, previewHeight };
  }

  if (!hasImageWidth || !hasImageHeight) {
    return {
      previewWidth: hasPreviewWidth ? previewWidth : null,
      previewHeight: hasPreviewHeight ? previewHeight : null,
    };
  }

  if (hasPreviewWidth) {
    return {
      previewWidth,
      previewHeight: (previewWidth * imageHeight) / imageWidth,
    };
  }

  if (hasPreviewHeight) {
    return {
      previewWidth: (previewHeight * imageWidth) / imageHeight,
      previewHeight,
    };
  }

  return { previewWidth: null, previewHeight: null };
}

export function cropRatioFromCroppedArea(
  croppedArea,
  previewWidth,
  previewHeight,
  imageWidth,
  imageHeight,
) {
  const resolvedPreviewSize = resolvePreviewSize({
    previewWidth,
    previewHeight,
    imageWidth,
    imageHeight,
  });
  const resolvedPreviewWidth = resolvedPreviewSize.previewWidth;
  const resolvedPreviewHeight = resolvedPreviewSize.previewHeight;

  if (
    !croppedArea ||
    typeof croppedArea !== "object" ||
    !Number.isFinite(croppedArea.width) ||
    !Number.isFinite(croppedArea.height) ||
    !Number.isFinite(resolvedPreviewWidth) ||
    !Number.isFinite(resolvedPreviewHeight) ||
    croppedArea.width <= 0 ||
    croppedArea.height <= 0 ||
    resolvedPreviewWidth <= 0 ||
    resolvedPreviewHeight <= 0
  ) {
    return null;
  }

  const preciseCropWidth = (croppedArea.width / 100) * resolvedPreviewWidth;
  const preciseCropHeight = (croppedArea.height / 100) * resolvedPreviewHeight;

  if (
    !Number.isFinite(preciseCropWidth) ||
    !Number.isFinite(preciseCropHeight) ||
    preciseCropWidth <= 0 ||
    preciseCropHeight <= 0
  ) {
    return null;
  }

  return preciseCropWidth / preciseCropHeight;
}

export function cropRatioFromCroppedAreaPixels(croppedAreaPixels) {
  if (
    !croppedAreaPixels ||
    typeof croppedAreaPixels !== "object" ||
    !Number.isFinite(croppedAreaPixels.width) ||
    !Number.isFinite(croppedAreaPixels.height) ||
    croppedAreaPixels.width <= 0 ||
    croppedAreaPixels.height <= 0
  ) {
    return null;
  }

  return croppedAreaPixels.width / croppedAreaPixels.height;
}

export function getCurrentCropRatioDetails({
  croppedArea,
  croppedAreaPixels,
  previewWidth,
  previewHeight,
  imageWidth,
  imageHeight,
} = {}) {
  const percentageRatio = cropRatioFromCroppedArea(
    croppedArea,
    previewWidth,
    previewHeight,
    imageWidth,
    imageHeight,
  );

  if (Number.isFinite(percentageRatio)) {
    return {
      currentCropRatio: percentageRatio,
      ratioSource: "croppedArea",
    };
  }

  const pixelRatio = cropRatioFromCroppedAreaPixels(croppedAreaPixels);

  if (Number.isFinite(pixelRatio)) {
    return {
      currentCropRatio: pixelRatio,
      ratioSource: "croppedAreaPixels",
    };
  }

  return {
    currentCropRatio: null,
    ratioSource: null,
  };
}

export function cropRatioMismatchMessage(currentCropRatio, expectedRatio) {
  const normalizedCurrentCropRatio = normalizeRatioValue(currentCropRatio);
  const normalizedExpectedRatio = normalizeRatioValue(expectedRatio);

  if (
    !Number.isFinite(normalizedCurrentCropRatio) ||
    !Number.isFinite(normalizedExpectedRatio)
  ) {
    return "The crop aspect ratio does not match the selected print size aspect ratio. Please adjust your crop and try again.";
  }

  return `The crop aspect ratio (${normalizedCurrentCropRatio.toFixed(4)}) does not match the selected print size aspect ratio (${normalizedExpectedRatio.toFixed(4)}). Please adjust your crop and try again.`;
}

export function userAgentLooksMobile(userAgent) {
  if (typeof userAgent !== "string" || userAgent.trim() === "") {
    return false;
  }

  return /Android.+Mobile|iPhone|iPod|Windows Phone|BlackBerry|Opera Mini|webOS/i.test(
    userAgent,
  );
}

export function buildCropRatioErrorProps(props = {}, details = {}) {
  const image = props.image || {};
  const existingCrop = image.crop || {};
  const printSize = details.printSize || {};
  const currentCropRatio = normalizeRatioValue(details.currentCropRatio);
  const expectedRatio = normalizeRatioValue(details.expectedRatio);
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : null;
  const resolvedPreviewSize = resolvePreviewSize({
    previewWidth: details.previewWidth,
    previewHeight: details.previewHeight,
    imageWidth: image.width,
    imageHeight: image.height,
  });
  const ratioDifference =
    Number.isFinite(currentCropRatio) && Number.isFinite(expectedRatio)
      ? Math.abs(currentCropRatio - expectedRatio)
      : null;
  const customSize = details.customSize || {};

  return {
    component: details.component,
    validation_source: "frontend",
    current_crop_ratio: currentCropRatio,
    expected_ratio: expectedRatio,
    ratio_difference: ratioDifference,
    crop_ratio_margin_of_error: CROP_RATIO_MARGIN_OF_ERROR,
    cropped_area_x: details.croppedAreaPixels?.x,
    cropped_area_y: details.croppedAreaPixels?.y,
    cropped_area_width: details.croppedAreaPixels?.width,
    cropped_area_height: details.croppedAreaPixels?.height,
    cropped_area_percentage_x: details.croppedArea?.x,
    cropped_area_percentage_y: details.croppedArea?.y,
    cropped_area_percentage_width: details.croppedArea?.width,
    cropped_area_percentage_height: details.croppedArea?.height,
    preview_width: resolvedPreviewSize.previewWidth,
    preview_height: resolvedPreviewSize.previewHeight,
    zoom: details.zoom,
    dpi: details.dpi,
    crop_orientation_portrait: details.cropOrientationPortrait,
    current_crop_ratio_source: details.currentCropRatioSource,
    is_custom_size: Boolean(details.isCustomSize),
    custom_size_width: customSize.width,
    custom_size_height: customSize.height,
    custom_size_unit: customSize.unit,
    print_size_title: printSize.title,
    print_size_width: printSize.width,
    print_size_height: printSize.height,
    print_size_unit: printSize.unit,
    image_id: image.id,
    image_key: image.key,
    image_width: image.width,
    image_height: image.height,
    image_original_ratio: props.source_ratio,
    image_existing_crop_x: existingCrop.cx,
    image_existing_crop_y: existingCrop.cy,
    image_existing_crop_width: existingCrop.cw,
    image_existing_crop_height: existingCrop.ch,
    force_ratio: props.force_ratio,
    media_type: props.media_type,
    location_pathname: typeof window !== "undefined" ? window.location.pathname : null,
    device_pixel_ratio:
      typeof window !== "undefined" ? window.devicePixelRatio : null,
    user_agent: userAgent,
    is_mobile: userAgentLooksMobile(userAgent),
    layout_is_mobile: details.isMobile,
  };
}
